import { SERVER_URL, getHeaders, handleResponse } from '../apiConfig';

const BIOMETRIC_API_BASE = `${SERVER_URL}/api/biometric`;

export const fetchStaffAttendance = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.date) query.append('date', params.date);
  if (params.startDate) query.append('startDate', params.startDate);
  if (params.endDate) query.append('endDate', params.endDate);
  if (params.staffId) query.append('staffId', params.staffId);
  const personType = params.personType || params.person_type;
  if (personType && personType !== 'all') query.append('personType', personType);
  const academicYearId = params.academicYearId || params.academic_year_id;
  if (academicYearId && academicYearId !== 'all') query.append('academicYearId', academicYearId);
  const standardId = params.standardId || params.standard_id;
  if (standardId && standardId !== 'all') query.append('standardId', standardId);
  const clusterId = params.clusterId || params.cluster_id;
  if (clusterId && clusterId !== 'all') query.append('clusterId', clusterId);
  const combination = params.combination;
  if (combination && combination !== 'all') query.append('combination', combination);

  const response = await fetch(`${BIOMETRIC_API_BASE}/attendance?${query.toString()}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const fetchMonthlyAttendanceMatrix = async (params = {}) => {
  const query = new URLSearchParams();
  const month = params.month || (params.year && params.month ? `${params.year}-${String(params.month).padStart(2, '0')}` : undefined);
  if (month) query.append('month', month);
  const personType = params.personType || params.person_type;
  if (personType) query.append('personType', personType);
  const academicYearId = params.academicYearId || params.academic_year_id;
  if (academicYearId && academicYearId !== 'all') query.append('academicYearId', academicYearId);
  const standardId = params.standardId || params.standard_id;
  if (standardId && standardId !== 'all') query.append('standardId', standardId);
  const clusterId = params.clusterId || params.cluster_id;
  if (clusterId && clusterId !== 'all') query.append('clusterId', clusterId);
  const combination = params.combination;
  if (combination && combination !== 'all') query.append('combination', combination);
  const uploadedOnly = params.uploadedOnly ?? params.uploaded_only;
  if (uploadedOnly !== undefined) query.append('uploadedOnly', uploadedOnly);

  const response = await fetch(`${BIOMETRIC_API_BASE}/monthly-matrix?${query.toString()}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const saveStaffAttendance = async (data) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/attendance`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const saveBatchCandidateAttendance = async (data) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/attendance/batch-candidate`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const fetchShiftSettings = async () => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/shift-settings`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const saveShiftSettings = async (data) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/shift-settings`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const deleteShiftSettings = async (id) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/shift-settings/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const recalculateStaffAttendance = async (data = {}) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/attendance/recalculate`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const deleteStaffAttendance = async (id) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/attendance/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const fetchBiometricMappings = async (params = {}) => {
  const query = new URLSearchParams();
  const academicYearId = params.academicYearId || params.academic_year_id;
  if (academicYearId && academicYearId !== 'all') query.append('academicYearId', academicYearId);
  const standardId = params.standardId || params.standard_id;
  if (standardId && standardId !== 'all') query.append('standardId', standardId);
  const clusterId = params.clusterId || params.cluster_id;
  if (clusterId && clusterId !== 'all') query.append('clusterId', clusterId);
  const combination = params.combination;
  if (combination && combination !== 'all') query.append('combination', combination);

  const url = query.toString() ? `${BIOMETRIC_API_BASE}/mappings?${query.toString()}` : `${BIOMETRIC_API_BASE}/mappings`;
  const response = await fetch(url, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const saveBiometricMapping = async (data) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/mappings`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const deleteBiometricMapping = async (id) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/mappings/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const pushSingleUserToBiometricDevice = async (id) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/mappings/${id}/push-to-device`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const deleteAllBiometricMappings = async () => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/mappings/clear-all`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const syncAllUsersToBiometricDevice = async (data = {}) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/mappings/sync-to-device`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const fetchBiometricDevices = async () => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/devices`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const saveBiometricDevice = async (data) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/devices`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const deleteBiometricDevice = async (id) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/devices/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const pingBiometricDevice = async (id) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/devices/${id}/ping`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const pingAllBiometricDevices = async () => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/devices/ping-all`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const requestBiometricAttendanceImport = async (data = {}) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/devices/import-attendance`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const clearBiometricDeviceUsers = async (data = {}) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/devices/clear-users`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const fetchRawPunchLogs = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.biometricUserId) query.append('biometricUserId', params.biometricUserId);
  if (params.date) query.append('date', params.date);
  if (params.limit) query.append('limit', params.limit);

  const response = await fetch(`${BIOMETRIC_API_BASE}/logs?${query.toString()}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const clearAllPunchLogs = async () => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/logs/clear-all`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const fetchServerNetworkInfo = async () => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/network-info`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const simulateBiometricPunch = async (biometric_user_id = '101', punch_state = 'IN') => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/simulate-punch`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ biometric_user_id, punch_state })
  });
  return handleResponse(response);
};


export const bulkUploadAttendance = async (records) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/attendance/bulk-upload`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ records })
  });
  return handleResponse(response);
};

export const bulkUploadMappings = async (records) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/mappings/bulk-upload`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ records })
  });
  return handleResponse(response);
};

export const autoImportBiometricMappings = async (data) => {
  const response = await fetch(`${BIOMETRIC_API_BASE}/mappings/auto-import`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};
